import React, { useState, useRef, useCallback } from "react";
import SampleQuestionsSidebar from "./SampleQuestionsSidebar";
import WelcomeScreen from "./WelcomeScreen";
import ChatMessages from "./ChatMessages";
import ChatInput from "./ChatInput";
import { useMsal } from "@azure/msal-react";

export default function AiAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [showWelcome, setShowWelcome] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { accounts } = useMsal();
  const userName = accounts?.[0]?.name || "User";
  const [chatHistory, setChatHistory] = useState([]);
  const [sessionId] = useState(() => `session-${Date.now()}`);

  // ── Streaming accumulator ────────────────────────────────────────────
  // We mutate the ref between frames and flush a snapshot into React state
  // via requestAnimationFrame so rapid SSE events don't cause excessive renders.
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const flushStream = useCallback(() => {
    rafRef.current = null;
    if (!streamRef.current) return;
    // Shallow-copy the accumulator (and the subagents map) for immutability
    const snapshot = {
      ...streamRef.current,
      subagents: { ...streamRef.current.subagents },
    };
    setMessages((prev) => {
      const updated = [...prev];
      const lastIdx = updated.length - 1;
      if (updated[lastIdx]?.isStreaming) {
        updated[lastIdx] = snapshot;
      }
      return updated;
    });
  }, []);

  const scheduleFlush = useCallback(() => {
    if (rafRef.current) return; // already scheduled
    rafRef.current = requestAnimationFrame(flushStream);
  }, [flushStream]);

  // ── SSE event processor ──────────────────────────────────────────────
  const processEvent = useCallback(
    (eventType, data) => {
      const s = streamRef.current;
      if (!s) return;

      switch (eventType) {
        case "thinking":
          s.thinking = (s.thinking || "") + data.content;
          scheduleFlush();
          break;

        case "subagent_start":
          s.subagents = {
            ...s.subagents,
            [data.id]: {
              name: data.name,
              description: data.description,
              status: "running",
              content: "",
              result: "",
              startedAt: Date.now(),
              completedAt: null,
            },
          };
          scheduleFlush();
          break;

        case "subagent_token":
          if (s.subagents[data.id]) {
            s.subagents = {
              ...s.subagents,
              [data.id]: {
                ...s.subagents[data.id],
                content: s.subagents[data.id].content + data.content,
              },
            };
            scheduleFlush();
          }
          break;

        case "subagent_end":
          if (s.subagents[data.id]) {
            s.subagents = {
              ...s.subagents,
              [data.id]: {
                ...s.subagents[data.id],
                status: "complete",
                result: data.result,
                completedAt: Date.now(),
              },
            };
            scheduleFlush();
          }
          break;

        case "token":
          s.text = (s.text || "") + data.content;
          scheduleFlush();
          break;

        case "done": {
          const reply = data.reply;
          if (reply && typeof reply === "object" && reply.text !== undefined) {
            s.text = reply.text ?? s.text ?? "No response received.";
            s.type = reply.type;
            s.data = reply.data;
          } else if (typeof reply === "string") {
            s.text = reply || s.text || "No response received.";
          } else {
            s.text = s.text || "No response received.";
          }
          s.isStreaming = false;
          // Immediate final flush
          flushStream();
          streamRef.current = null;

          if (Array.isArray(data.history)) {
            setChatHistory(data.history);
          }
          break;
        }

        case "error":
          s.text = data.message || "Something went wrong.";
          s.isStreaming = false;
          flushStream();
          streamRef.current = null;
          break;

        default:
          break;
      }
    },
    [scheduleFlush, flushStream]
  );

  // ── Send handler (SSE streaming) ─────────────────────────────────────
  const handleSend = async (text) => {
    if (!text.trim() || isLoading) return;

    const userId = localStorage.getItem("user_id");
    const newUserMessage = { role: "human", content: text };
    const updatedHistory = [...chatHistory, newUserMessage];

    // Prepare the streaming placeholder (separate copies for ref and state)
    const seed = {
      role: "ai",
      text: "",
      thinking: "",
      subagents: {},
      isStreaming: true,
    };
    streamRef.current = { ...seed, subagents: {} };

    setMessages((prev) => [
      ...prev,
      { role: "user", text },
      { ...seed, subagents: {} },
    ]);
    setInput("");
    setShowWelcome(false);
    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/chat/stream/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: updatedHistory,
            session_id: sessionId,
            user_id: userId,
          }),
        }
      );

      if (!response.ok) {
        const errBody = await response.text();
        console.error("API error:", response.status, errBody);
        throw new Error(`Server error ${response.status}`);
      }

      // ── Read the SSE stream ──────────────────────────────────────────
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop(); // keep any incomplete trailing part

        for (const part of parts) {
          if (!part.trim()) continue;
          const lines = part.split("\n");
          let eventType = "";
          let eventData = "";

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7);
            } else if (line.startsWith("data: ")) {
              eventData += (eventData ? "\n" : "") + line.slice(6);
            }
          }

          if (eventType && eventData) {
            try {
              processEvent(eventType, JSON.parse(eventData));
            } catch (e) {
              console.error("SSE parse error:", e, eventData);
            }
          }
        }
      }

      // Drain any leftover buffer
      if (buffer.trim()) {
        const lines = buffer.split("\n");
        let eventType = "";
        let eventData = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) eventType = line.slice(7);
          else if (line.startsWith("data: "))
            eventData += (eventData ? "\n" : "") + line.slice(6);
        }
        if (eventType && eventData) {
          try {
            processEvent(eventType, JSON.parse(eventData));
          } catch (_) {
            /* ignore trailing parse errors */
          }
        }
      }
    } catch (error) {
      console.error("handleSend error:", error);
      if (streamRef.current) {
        streamRef.current.text = "Something went wrong. Please try again.";
        streamRef.current.isStreaming = false;
        flushStream();
        streamRef.current = null;
      } else {
        setMessages((prev) => [
          ...prev.filter((m) => !(m.isStreaming && m.role === "ai")),
          { role: "ai", text: "Something went wrong. Please try again." },
        ]);
      }
    } finally {
      setIsLoading(false);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInput("");
    setShowWelcome(true);
    setChatHistory([]);
    setIsLoading(false);
    streamRef.current = null;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  return (
    <div className="flex h-full w-full overflow-hidden bg-gray-50">
      {/* Left: Sample Questions */}
      <SampleQuestionsSidebar
       onQuestionClick={(text) => handleSend(text)}
         onNewChat={handleNewChat}
      />

      {/* ── Right: Chat Area ── */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Panel Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 shrink-0">
          <div className="flex gap-1 items-center align-center">
            <h2 className="text-base font-semibold text-gray-800">Pulse AI Assistant</h2>
            <div className="px-1 py-1 rounded bg-[#FB7185] text-white whitespace-nowrap flex text-center items-center h-[20px]">
              <span className="text-xs">Beta Version</span>
            </div>
          </div>
          <button className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages / Welcome */}
        <div className="flex-1 px-6 overflow-y-auto py-6">
          {showWelcome && messages.length === 0 ? (
            <WelcomeScreen onSuggestionClick={handleSend} />
          ) : (
            <ChatMessages 
              messages={messages} 
              userName={userName} 
              isLoading={isLoading} 
            />
          )}
        </div>

        {/* Input */}
        <ChatInput
          value={input}
          onChange={setInput}
          onSend={handleSend}
          isLoading={isLoading} 
        />
      </div>
    </div>
  );
}
